import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { CheckCircle, Cancel, Delete } from '@mui/icons-material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';

const ALL_PLAN_REQUESTS_QUERY = gql`
  query AllPlanRequests {
    allPlanRequests {
      id
      status
      rejection_reason
      reviewed_at
      created_at
      coupon_code
      discount_percent
      base_amount
      final_amount
      currency
      user {
        id
        full_name
        email
      }
      plan {
        id
        name
      }
      reviewer {
        id
        full_name
      }
    }
  }
`;

const APPROVE_MUTATION = gql`
  mutation ApprovePlanRequest($id: ID!) {
    approvePlanRequest(id: $id) {
      id
      status
      reviewed_at
      reviewer {
        full_name
      }
    }
  }
`;

const REJECT_MUTATION = gql`
  mutation RejectPlanRequest($id: ID!, $rejection_reason: String!) {
    rejectPlanRequest(id: $id, rejection_reason: $rejection_reason) {
      id
      status
      rejection_reason
      reviewed_at
      reviewer {
        full_name
      }
    }
  }
`;

const DELETE_PLAN_REQUESTS_MUTATION = gql`
  mutation DeletePlanRequests($ids: [ID!]!) {
    deletePlanRequests(ids: $ids) {
      id
    }
  }
`;

function PlanRequestList() {
  const { t } = useTranslation(['common', 'buttons']);
  const [rejectDialog, setRejectDialog] = useState({ open: false, requestId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data, loading, refetch } = useQuery(ALL_PLAN_REQUESTS_QUERY, {
    fetchPolicy: 'network-only'
  });

  const closeRejectDialog = () => {
    setRejectDialog({ open: false, requestId: null });
    setRejectReason('');
  };

  const [approveMutation, { loading: approving }] = useMutation(APPROVE_MUTATION, {
    onCompleted: () => refetch()
  });

  const [rejectMutation, { loading: rejecting }] = useMutation(REJECT_MUTATION, {
    onCompleted: () => {
      refetch();
      closeRejectDialog();
    }
  });

  const [deleteMutation] = useMutation(DELETE_PLAN_REQUESTS_MUTATION, {
    onCompleted: () => {
      setSelectedIds([]);
      setDeleteModalOpen(false);
      refetch();
    }
  });

  const handleApprove = (id) => {
    approveMutation({ variables: { id } });
  };

  const openRejectDialog = (id) => {
    setRejectDialog({ open: true, requestId: id });
    setRejectReason('');
  };

  const handleReject = () => {
    if (!rejectReason.trim()) return;
    rejectMutation({ variables: { id: rejectDialog.requestId, rejection_reason: rejectReason.trim() } });
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleAll = (requests) => {
    if (selectedIds.length === requests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(requests.map((r) => r.id));
    }
  };

  const handleConfirmDelete = () => {
    deleteMutation({ variables: { ids: selectedIds } });
  };

  const statusLabels = {
    PENDING:  { label: t('plan.requests.statusLabels.PENDING', { ns: 'common' }),  color: 'warning' },
    APPROVED: { label: t('plan.requests.statusLabels.APPROVED', { ns: 'common' }), color: 'success' },
    REJECTED: { label: t('plan.requests.statusLabels.REJECTED', { ns: 'common' }), color: 'error' }
  };

  const requests = data?.allPlanRequests ?? [];
  const fmtMoney = (v, c) => {
    if (v == null) return '—';
    return c === 'USD' ? `$${Number(v).toFixed(2)} USD` : `$${Number(v).toLocaleString('es-CO')} COP`;
  };

  return (
    <>
      <SEOPageTitle title={t('plan.requests.title', { ns: 'common' })} />

      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('plan.requests.title', { ns: 'common' })}
          </Typography>
          {selectedIds.length > 0 && (
            <Tooltip title={t('plan.requests.deleteTooltip', { ns: 'common', count: selectedIds.length })}>
              <IconButton
                color="error"
                onClick={() => setDeleteModalOpen(true)}>
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {loading && (
          <Stack alignItems="center" py={6}>
            <CircularProgress color="secondary" />
          </Stack>
        )}
        {!loading && requests.length === 0 && (
          <Alert severity="info">{t('plan.requests.empty', { ns: 'common' })}</Alert>
        )}
        {!loading && requests.length > 0 && (
          <TableContainer
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.length > 0 && selectedIds.length < requests.length}
                      checked={requests.length > 0 && selectedIds.length === requests.length}
                      onChange={() => handleToggleAll(requests)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('plan.requests.columns.user', { ns: 'common' })}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('plan.requests.columns.plan', { ns: 'common' })}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('plan.requests.columns.charge', { ns: 'common', defaultValue: 'Cobro' })}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('plan.requests.columns.status', { ns: 'common' })}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('plan.requests.columns.requestDate', { ns: 'common' })}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('plan.requests.columns.reviewedBy', { ns: 'common' })}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('plan.requests.columns.rejectionReason', { ns: 'common' })}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">{t('plan.requests.columns.actions', { ns: 'common' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((req) => {
                  const statusCfg = statusLabels[req.status] ?? { label: req.status, color: 'default' };
                  const isPending = req.status === 'PENDING';
                  const createdDate = new Date(req.created_at).toLocaleDateString('es-CO');
                  const isSelected = selectedIds.includes(req.id);

                  return (
                    <TableRow key={req.id} hover selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleSelect(req.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {req.user.full_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {req.user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{req.plan.name}</Typography>
                      </TableCell>
                      <TableCell>
                        {req.final_amount != null ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>{fmtMoney(req.final_amount, req.currency)}</Typography>
                            {req.coupon_code && (
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>{fmtMoney(req.base_amount, req.currency)}</Typography>
                                <Chip size="small" label={`${req.coupon_code}${req.discount_percent ? ` · -${req.discount_percent}%` : ''}`} sx={{ height: 18, backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 700, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />
                              </Stack>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusCfg.label}
                          color={statusCfg.color}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{createdDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {req.reviewer?.full_name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 200 }}>
                          {req.rejection_reason ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isPending ? (
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle fontSize="small" />}
                              disabled={approving}
                              onClick={() => handleApprove(req.id)}>
                              {t('planRequest.approve', { ns: 'buttons' })}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<Cancel fontSize="small" />}
                              onClick={() => openRejectDialog(req.id)}>
                              {t('planRequest.reject', { ns: 'buttons' })}
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('plan.requests.reviewed', { ns: 'common' })}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      {/* Dialog de rechazo */}
      <Dialog open={rejectDialog.open} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('plan.requests.rejectDialogTitle', { ns: 'common' })}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={t('plan.requests.rejectReasonLabel', { ns: 'common' })}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeRejectDialog}>{t('cancel', { ns: 'buttons' })}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectReason.trim() || rejecting}
            onClick={handleReject}>
            {t('planRequest.confirmReject', { ns: 'buttons' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de borrado */}
      <ConfirmDeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        count={selectedIds.length}
      />
    </>
  );
}

export default PlanRequestList;
